import {defineStore} from "pinia";
import {LandingPageService} from "../services/landingPage.service";
import tinycolor from "tinycolor2";
import {OrganizationService} from "../services/organization.service";
import {ApplicationHelper} from "../helpers/ApplicationHelper";
import {ResourceService} from "../services/resource.service";
import {WebsiteLayoutService} from "../services/websiteLayout.service";
import {BrandService} from "../services/brand.service";
// import {Debugger} from "node:inspector";

export const LANDING_PAGE = 'landing_page';
export const DEFAULT_PRIMARY_COLOR = '#10be5a';
export const DEFAULT_ACCENT_COLOR = '#3a4473';
export const useLandingPageStore = defineStore('landingPage', {
  state: () => ({
    landingPageService: null,
    websiteService: null,
    resourceService: null,
    brandService: null,
    organizationService: new OrganizationService(),
    organizationId: null,
    organization: null,
    hasPaymentProcessor: false,


    resources: [],
    imageCache: new Map(),
    publishedLayout: {},
    publishedPageLoaded: false,
    draftLayout: {},
    initialLayoutData: "", // Store reference to og data so we can reset if needed


    donationTargets: [],
    subscriptionsEnabled: true,
    subscriptionIntervals: [],
    layoutId: null,
    logo: "",
    landingPageOptions: {

      /**
       *    Main Data Structure for content editing.
       *    All of the static values (labels, select options, etc) get stripped out of the object when saving.
       *    Only variables get saved in the backend so all the stripped values can be updated with the frontend for backwards compatability.
       *    Don't change object keys.
       *
       *    To add a new option to the landing page builder, update this object. Do not change JSON keys or old data will be broken
       *
       *    This object is for static reference only and should not be edited. (Issues with copying by reference w/ nested objects)
       *    When page data gets loaded this object gets cloned and merged with the page data.
       *    This is to prevent bleed over between orgs, was having issues w/ copying by reference with nested JSON objects.
       *
       *
       *    FORMAT:
       *    sectionName: {
       *         sectionTitle: "Page Header", => Display Heading for section
       *         index: 0, => Component static index to prevent them getting mixed up when saving. This will be used in future to be able to change ordering of components
       *         values: { => Items in the Sections to edit. Dynamically rendered in LandBuilderControlBar
       *           valueName: {
       *             label: "Header Logo", => Label to display
       *             type: "switch",   => Control type options
       *                                 'switch' => Boolean Toggle switch
       *                                 'text' => Single line test
       *                                 'textarea' => larger text input, takes 'rows' key
       *                                 'image' => Image picker modal. Required ID key to store the blob id of the image
       *                                 'color' => Color picker modal (not used atm). Required 'colorValue' key
       *                                 'image-color' => Image and Color picker when either can be used (ie: backgrounds). Requires both 'id' and 'colorValue' keys
       *                                 'select' => Dropdown select, needs 'options' key/
       *             value: true, => Stored value of the item
       *           }
       *         }
       *       },
       */
      header: {
        sectionTitle: "Header",
        values: {
          logo: {
            label: "Show Logo",
            type: "switch",
            value: true,
          },

          logoUrl: {
            label: "Website URL",
            type: "text",
            placeholder: "example.com",
            errorMessage: "Please enter a valid URL",
            validation: (value) => {
              return ApplicationHelper.validateUrl(value);
            }
          },
        },
      },
      hero_banner: {
        sectionTitle: "Hero Section",
        values: {
          title: {
            label: "Title",
            type: "text",
            placeholder: "Title",
          },
          subtitle: {
            label: "Subtitle",
            type: "textarea",
            rows: 2,
            placeholder: "Subtitle",
          },
          bannerImage: {
            label: "Image",
            type: "image",
            resourceId: null,
            ratio: 3,
          },
          // bannerImagePosition: {
          //   label: "Image Position",
          //   type: "select",
          //   options: {
          //     center: "Center",
          //     top: "Top",
          //     bottom: "Bottom",
          //     right: "Right",
          //     left: "Left"
          //   },
          //   value: "Center"
          // }
        },
      },
      video: {
        sectionTitle: "Video",
        values: {
          title: {
            label: "Header",
            placeholder: "Title",
            type: "text",
          },
          subtitle: {
            label: "Subheading",
            placeholder: "Subtitle",
            type: "text",
          },
          video: {
            label: "YouTube URL",
            type: "text",
            placeholder: "https://youtube.com/watch?v=xxxxxxxxxxx",
            errorMessage: "Please enter a valid URL",
            validation: (value) => {
              return ApplicationHelper.validateUrl(value);
            }
          },
          autoplay: {
            label: "Autoplay Video",
            type: "switch",
            value: false
          },
          muted: {
            label: "Muted",
            type: "switch",
            value: false
          }
        }
      },
      article: {
        sectionTitle: "About Section",
        index: 2,
        values: {
          title: {
            label: "Header",
            type: "text",
            placeholder: "Title",
          },
          bodyText: {
            label: "Body Text",
            type: "rich-text",
            rows: 5,
            placeholder: "Tell us about yourself.",
          },
          layout: {
            label: "Layout",
            type: "select",
            options: {
              imageLeft: "Image Left",
              imageRight: "Image Right"
            },
            value: "imageLeft",
          },
          articleImage: {
            label: "Image",
            type: "image",
            resourceId: null,
            ratio: 1, // Default Aspect Ratio
          },
        }
      },
      donatable_grid: {
        sectionTitle: "Campaigns",
        values: {
          title: {
            label: "Title",
            type: "text",
            placeholder: "Ways to Give",
          },

          layout: {
            label: "Card Layout",
            type: "select",
            options: {
              grid: "Grid",
              list: "List",
              carousel: "Carousel",
            },
            value: "list",
          },

          order: {
            label: "Order",
            type: "campaign-sort-order", // Custom option to order the campaigns,
            value: []
          }
        }
      },
      footer: {
        sectionTitle: "Footer",
        index: 4,
        values: {
          showFooter: {
            label: "Full Footer",
            type: "switch",
          },
          logo: {
            label: "Logo",
            type: "switch",
          },
          socials: {
            label: "Social Links",
            type: "switch",
          },
        }
      }
    },
    saving: false,
    dirtyDraft: false,
    publishing: false,
    sortableComponents: [],
    unpublishedChanges: false,

    // BRANDING
    brand: {
      color_primary: "",
      color_accent: "",
      color_primary_text: "",
      color_accent_text: ""
    },
    // Local copy of brand values to retain changes before a save
    localBrand: {
      color_primary: "",
      color_accent: "",
      color_primary_text: "",
      color_accent_text: ""
    },

  }),

  actions: {
    removedComponents: [],
    setOrganizationId(id) {
      const incoming = id != null ? String(id) : null;
      const existing = this.organizationId != null ? String(this.organizationId) : null;
      if (incoming && existing !== incoming) {
        this.clearData();
        this.brand = {};
        this.localBrand = {};
      }
      if (id) {
        this.organizationId = id;
        this.landingPageService = new LandingPageService(id);
        this.websiteService = new WebsiteLayoutService(id);
        this.resourceService = new ResourceService(id);
        this.brandService = new BrandService(id);
      } else {
        throw `Organization ID is not set: ${id}`;
      }
    },

    // Publishes the current working draft, then loads the new draft record
    publishDraft() {
      this.publishing = true;

      return this.saveCurrentDraft() // Update the current changes
        .then(() => this.websiteService.publishCurrentDraft())// Publish the current draft
        .then(() => this.loadCurrentDraft()) // Reload draft
        .then(() => {
          this.publishing = false;
          this.unpublishedChanges = false;
          return true;
        });
    },

    loadPublishedPage() {
      if (this.organizationId && this.publishedPageLoaded) {
        // Prevent unnecessary page reloads
        return Promise.resolve(true);
      }
      this.publishedLayout = null;

      let promise = null;

      // Loading the landing page differently whether we're getting the slug from the url
      // or letting backend determine by subdomain
      if (this.organizationId) {
        promise = this.websiteService.publishedLayout();
      } else {
        promise = new WebsiteLayoutService(null).publishedLayoutNoSlug();
      }
      if (!promise) return null;

      return promise
        .then((result) => {
          const data = result?.json?.data;
          if (data && data.organization && data.organization.slug) {
            if (this.organizationId == null) {
              this.setOrganizationId(data.organization.slug);
            }
            this.organization = data.organization;
            this.loadResources();

            if (data.page_layout) {
              this.publishedLayout = this.transformLayoutValues(data.page_layout);
              this.layoutId = data.page_layout.id;
            }

            if (data.payment_processor) {
              this.hasPaymentProcessor = true;
              this.subscriptionsEnabled = data.payment_processor.subscriptions_enabled;
              this.subscriptionIntervals = data.payment_processor.enabled_subscription_intervals;
            }
            this.publishedPageLoaded = true;

          }
          return true;
        });
    },

    loadCurrentDraft() {
      this.draftLayout = {};
      this.loadResources();
      return this.websiteService.draftLayout()
        .then((result) => {
          const data = result?.json?.data;

          if (data) {
            this.organization = data.organization;

            if (data.page_layout) {
              this.initialLayoutData = JSON.stringify(data.page_layout); //giving this the raw backend data
              this.draftLayout = this.transformLayoutValues(data.page_layout);

              this.layoutId = data.page_layout.id;
            }
          }

          return true;
        });
    },

    saveCurrentDraft() {
      this.dirtyDraft = false;
      this.saving = true;


      this.archiveComponents();

      return this.websiteService
        .update(this.draftLayout.id, {website_layout: this.stripKeys(this.draftLayout)})
        .then(() => {
          this.unpublishedChanges = true;
          // Update initialLayoutData after successful save
          this.initialLayoutData = JSON.stringify(this.stripKeys(this.draftLayout));

          // Only reorder if all components have ids
          const landingPage = this.draftLayout.pages?.find(
            page => page.page_type === LANDING_PAGE
          );

          const newComponents = landingPage.components.filter(c => !c.archived && !c.id);

          // If there are new components without IDs, wait for them to reload
          if (newComponents.length) {
            return this.loadCurrentDraft().then(() => this.saveComponentOrder());
          }

          return this.saveComponentOrder();
        })
        .finally(() => {
          this.saving = false;
        });
    },

    // Loads image resources and funds/ campaigns
    loadResources() {
      if (!this.resources || this.resources.length === 0) {
        this.resourceService.index()
          .then((res) => {
            this.resources = res.data;
          });
      }

      // Prevent multiple calls
      if (!this.donationTargets || this.donationTargets.length === 0) {
        this.loadDonatableTargets();
      }

      this.brandService.index()
        .then((res) => {
          const brand = res.data[0];
          this.setBrandColors(brand);
        });
    },

    // Take a layout object from backend & maps default values, labels, etc to the object
    // So we don't store extra values in backend object, makes changes a lot eas
    transformLayoutValues(layout) {
      //get the pages
      layout.pages?.forEach(page => {
        // NEW - Sort components by page_order from backend
        // if (page.components) {
        //   page.components.sort((a, b) => (a.page_order ?? 0) - (b.page_order ?? 0));
        // }

        // get each component
        page.components?.forEach(component => {
          const defaults = this.landingPageOptions[component.component_type]?.values;
          const currentValues = component.component_data;

          if (currentValues && defaults) {
            // Create a new ordered object based on defaults order
            const orderedData = {};

            // First, add all keys from defaults in their original order
            for (const key in defaults) {
              if (defaults[key] != null) {
                orderedData[key] = this.mergeDeep(currentValues[key], defaults[key]);
              }
            }

            // Replace component_data with the ordered version
            component.component_data = orderedData;
          }
        });
      });

      return layout;
    },

    loadDonatableTargets() {
      if (!this.organizationId) return;
      return this.organizationService.getDonationTargets(this.organizationId, false, "Landing")
        .then((res) => {
          this.donationTargets = res.json;
          return true;
        }).catch((e) => {
          console.error("error inside loadDonatableTargets:", e);
          this.donationTargets = [];
          return false;
        });
    },

    // This will reset changes done since the last page load
    // But maintain object references to the draft layout json by not replacing the object
    resetDraftChanges() {
      //mergeDeep doesn't remove props that exist in the target
      //   this.mergeDeep(this.draftLayout, JSON.parse(this.initialLayoutData)); <- This stopped working.


      this.draftLayout = this.transformLayoutValues(JSON.parse(this.initialLayoutData)); //inspired by loadCurrentDraft
      this.dirtyDraft = false;
    },

    // Clears out cached data to prevent data bleed between organizations when switching
    clearData() {
      this.landingPage = {};
      this.resources = [];
      this.layoutId = null;
      this.donationTargets = [];
      this.removedComponents = [];
    },

    getDonatable(donatableId, model) {
      let params = new URLSearchParams();
      params.set('donatable_id', donatableId);
      params.set('model', model);

      return this.organizationService.getDonatable(this.organizationId, params)
        .then((response) => response.json?.data);
    },

    getDonatableBySlug(slug) {
      let params = new URLSearchParams();
      params.set('slug', slug);

      return this.organizationService.getDonatable(this.organizationId, params)
        .then((response) => response.json?.data);
    },

    uploadImage(imageFile) {
      return Promise.resolve()
        .then(() => this.resourceService.createImageResource(imageFile))
        .then((uploadResponse) => {
          if (uploadResponse.status === 200) {
            this.resourceService.index()
              .then((res) => {
                this.resources = res.data;
              });
            // Successful creation returns an array of resource ids, return the first one
            return uploadResponse.json["resource_ids"][0] || true;
          }
          return false;
        })
        .catch((e) => {
          console.error("error inside uploadImage:", e);
          return false;
        });
    },

    uploadCroppedImage(base64String, originalName = "cropped-image.png") {
      // 1) data‑URL → Blob
      const blob = this.base64ToBlob(base64String);

      // 2) wrap Blob in a real File, giving it the original filename (minus its old extension + “.png”)
      const baseName = originalName.replace(/\.[^.]*$/, ""); // strip any existing extension
      const file = new File(
        [blob],
        originalName,
        {type: blob.type}
      );


      // 3) upload that File to the server
      return this.uploadImage(file).then(res => {
        this.resourceService.index()
          .then((res) => {
            this.resources = res.data;
          });
        return res; // return resource id
      });
    },

    base64ToBlob(base64) {
      const byteString = atob(base64.split(',')[1]);
      const mimeString = base64.split(',')[0].split(':')[1].split(';')[0];
      const ab = new ArrayBuffer(byteString.length);
      const ia = new Uint8Array(ab);
      for (let i = 0; i < byteString.length; i++) {
        ia[i] = byteString.charCodeAt(i);
      }
      return new Blob([ab], {type: mimeString});
    },



    // removeImage(image) {
    //   if (!image || !image.metadata) {
    //     return;
    //   }
    //   this.images = this.images.filter(i => i !== image);
    //   this.pageLayoutService.removeImage(
    //     this.layoutId,
    //     image.metadata.blob_id
    //   )
    //     .then(() => true);
    // },

    //This uses image.id and resourceService instead of image.metadata
    //UploadImage uses this resource too
    removeImage(image) {
      if (!image || !image.id) {
        return;
      }
      this.resources = this.resources.filter(r => r.id !== image.id);

      const imageUrl = `${this.resourceService.endpoint}/${image.id}`;
      this.resourceService.destroy(imageUrl) //<- sets archive to true in backend
        .catch(err => {
          // Re-add if this fails
          this.resources.push(image);
        });
    },

    // Strips keys that should be static on the frontend, in case of type/ label changes.
    // The backend should only store the selected values that get displayed
    // Static keys get added back in whenever the object is loaded again
    stripKeys(object) {
      const keysToRemove = ["type", "label", "sectionTitle", "index", "options", "rows"];
      const objectCopy = {...object};
      return this.removeKeys(objectCopy, keysToRemove);
    },

    /**
     * Strips nested keys from JSON object
     * @param obj
     * @param keysToRemove
     * @returns {*}
     */
    removeKeys(obj, keysToRemove) {
      // Create a new object to avoid mutating the original
      let newObj = Array.isArray(obj) ? [] : {};

      for (let key in obj) {
        // Check if the current key should be removed
        if (keysToRemove.includes(key)) {
          continue;
        }

        // If the value is an object (including arrays), recursively remove keys
        if (typeof obj[key] === 'object' && obj[key] !== null) {
          newObj[key] = this.removeKeys(obj[key], keysToRemove); // Recursive call
        } else {
          newObj[key] = obj[key]; // Keep the value if not an object
        }
      }

      return newObj;
    },

    /**
     * Deep merge of JSON objects
     * https://stackoverflow.com/questions/27936772/how-to-deep-merge-instead-of-shallow-merge
     * @param target
     * @param source
     * @returns {*}
     */
    mergeDeep(target, source) {
      if (target === null || target === undefined) target = {};
      if (typeof target === 'object' && typeof source === 'object') {
        for (const key in source) {
          if (typeof source[key] === 'object' && source[key] !== null) {
            if (target[key] === null || target[key] === undefined || typeof target[key] !== 'object') {
              Object.assign(target, {[key]: {}});
            }
            this.mergeDeep(target[key], source[key]);
          } else {
            // Only assign from source if target doesn't have this key
            if (!(key in target)) {
              Object.assign(target, {[key]: source[key]});
            }
          }
        }
      }

      return target;
    },

    /**
     * Recursive function that will strip invalid entries from the landing page data
     * this will prevent legacy options that get removed from breaking the UI
     * @param obj
     */
    removeInvalidObjects(obj) {
      for (const key in obj) {
        if (obj.hasOwnProperty(key)) {
          const value = obj[key];

          // If the current value is an object, check its keys
          if (typeof value === 'object' && value !== null) {
            // Recursively check nested objects
            this.removeInvalidObjects(value);

            // After recursion, check if this object has "value" but not "type"
            // This removes legacy entries that are not present in the landing builder any more
            if (value.hasOwnProperty('value') && !value.hasOwnProperty('type')) {
              delete obj[key];
            }
          }
        }
      }
    },

    // Sets the custom sort order from the Campaign sort component
    setDonatableCustomSortOrder(targetsList, component) {
      if (!component) return;
      let customSortObject = [];
      targetsList.forEach(function (target, index) {
        customSortObject.push({
          order: index,
          uuid: target.uuid,
          name: target.name // this is for debugging, only need uuid and order
        });
      });

      component.component_data.order.value = customSortObject;
      this.dirtyDraft = true;
      return true;
    },

    /**
     * Take component data and strips out the values from the json
     * in the proper format to v-bind to a vue component
     * {
     *   "field_1": {
     *     "placeholder": "test",
     *     "other_stuff": [],
     *     "value" : "real value"
     *   },
     *   "field_2": {
     *     "placeholder": "Test",
     *     "extras": [],
     *     "another_thing": "whatever",
     *     "value": "real value"
     *   }
     * }
     * =>
     * {
     *   "field_1": "real value",
     *   "field_2": "real_value"
     * }
     * @param component => component json
     * @returns {{}}
     */
    getComponentValues(component) {
      if (!component || !component.component_data) return {};

      const result = {};
      const json = component.component_data;

      for (const key in json) {
        if (json[key] && typeof json[key] === 'object') {
          if ('value' in json[key]) {
            result[key] = json[key].value;
          }
          if ('resourceId' in json[key]) {
            result[key] = json[key].resourceId;
          }
        }
      }
      return result;
    },

    // BRANDING / COLOUR FUNCTIONS

    // Sets the primary and accent colors from the record, calculates the light/ dark variants
    setBrandColors(record) {
      if (!record.color_primary && !record.color_secondary) {
        this.setDefaultBrand();
      } else {
        this.brand = record;

        this.localBrand = Object.keys(this.brand)
          .filter(key => key.toString().startsWith('color'))
          .reduce((result, key) => {
            result[key] = this.brand[key];
            return result;
          }, {});

        this.localBrand.logo = this.brand.logo ? { ...this.brand.logo } : {};
        this.localBrand.favicon = this.brand.favicon ? { ...this.brand.favicon } : {};
        this.updateColors();
      }
    },

    setDefaultBrand() {
      this.localBrand = {
        color_primary: DEFAULT_PRIMARY_COLOR,
        color_accent: DEFAULT_ACCENT_COLOR,
      };
      this.updateColors();
      // Overrides for Allgive branding
      this.localBrand.color_primary_light = "#45ef8d";
      this.localBrand.color_primary_dark = "#08602d";
    },

    setDefaultTextColors() {
      this.localBrand.color_primary_text = this.getContrastTextColor(this.localBrand.color_primary);
      this.localBrand.color_accent_text = this.getContrastTextColor(this.localBrand.color_accent);
    },

    // Automatically calculate light and dark variants of the selected colors.
    updateColors() {
      const primary = tinycolor(this.localBrand.color_primary);
      if (primary.isValid()) {
        this.localBrand.color_primary_light = tinycolor(this.localBrand.color_primary).lighten(20).toHexString();
        this.localBrand.color_primary_dark = tinycolor(this.localBrand.color_primary).darken(20).toHexString();
      }
      const secondary = tinycolor(this.localBrand.color_secondary);
      if (secondary.isValid()) {
        this.localBrand.color_secondary_light = tinycolor(this.localBrand.color_secondary).lighten(20).toHexString();
        this.localBrand.color_secondary_dark = tinycolor(this.localBrand.color_secondary).darken(20).toHexString();
      }
      const accent = tinycolor(this.localBrand.color_primary);
      if (accent.isValid()) {
        this.localBrand.color_accent_light = tinycolor(this.localBrand.color_accent).lighten(20).toHexString();
        this.localBrand.color_accent_dark = tinycolor(this.localBrand.color_accent).darken(20).toHexString();
      }

      this.setDefaultTextColors();
    },
    // Calculates appropriate text color based on luminance
    getContrastTextColor(color) {
      const darkText = "#212121";
      const lightText = "#EBEBEB";

      const colorObj = tinycolor(color);
      if (!colorObj.isValid()) {
        return darkText;
      }
      const luminance = colorObj.getLuminance();

      return luminance > 0.5 ? darkText : lightText;
    },

    getFooterBackgroundColor() {
      const accent = this.localBrand?.color_accent;
      if (accent) {
        const color = tinycolor(accent);
        if (color.isValid() && color.getLuminance() < 0.85) {
          return accent;
        }
      }
      return this.localBrand?.color_primary || DEFAULT_PRIMARY_COLOR;
    },


    resetBrand() {
      // Clone local brand from original
      this.localBrand = Object.keys(this.brand)
        .filter(key => key.toString().startsWith('color'))
        .reduce((result, key) => {
          result[key] = this.brand[key];
          return result;
        }, {});

      this.updateColors();
    },

    applyCssVarsToRoot() {
      const element = document.documentElement;
      const vars = this.cssVars; // getter above
      Object.entries(vars).forEach(([k, v]) => element.style.setProperty(k, String(v)));
    },

    saveGlobalSettings() {
      this.savingBrand = true;
      const params = {
        "color_primary": this.localBrand.color_primary || DEFAULT_PRIMARY_COLOR,
        "color_accent": this.localBrand.color_accent || DEFAULT_ACCENT_COLOR,
        "logo_id": this.localBrand.logo?.id,
        "favicon_id": this.localBrand.favicon?.id
      };
     return this.brandService.update(
        this.brand.id,
        {"brand": params}
      ).then((res) => {
        this.setBrandColors(res.json);
        this.savingBrand = false;
      });
    },

    /* COMPONENTS ORDERING*/

    saveComponentOrder() {
      const landingPage = this.draftLayout?.pages?.find(
        p => p.page_type === LANDING_PAGE
      );
      const staticComponents = ['header', 'hero_banner', 'footer'];
      const active = landingPage.components.filter(c => !c.archived);

      const body = active.filter(
        c => !staticComponents.includes(c.component_type)
      );

      const header = active.find(c => c.component_type === 'header');
      const hero = active.find(c => c.component_type === 'hero_banner');
      const footer = active.find(c => c.component_type === 'footer');

      const normalized = [
        header,
        hero,
        ...body,
        footer
      ];

      // Set order based on index of array
      normalized.forEach((c, idx) => {
        c.order = idx;
      });

      // Format and send to backend
      const componentsForBackend = normalized.map((c, idx) => ({
        id: c.component_id || c.component?.id || c.id, // Try different ID fields
        page_order: idx
      }));


      return this.landingPageService.reorderLandingPage(
        this.organizationId,
        landingPage.id,
        { components: componentsForBackend }
      );
    },

    addComponentToDraft(componentData) {
      const landingPage = this.draftLayout.pages.find( page => page.page_type === LANDING_PAGE );
      // Filter out archived components
      const active = landingPage.components.filter(c => !c.archived);
      const footer = active.find(c => c.component_type === 'footer');
      // Create new component
      const newComponent = {
        component_type: componentData.component_type,
        component_data: componentData.component_data,
        archived: false,
        order: footer.order,
      };
      footer.order = footer.order + 1;
      // Push new component
      landingPage.components.push(newComponent);

      // Normalize everything
      this.normalizeLandingPageComponents();
      this.dirtyDraft = true;
    },

    normalizeLandingPageComponents() {
      const landingPage = this.draftLayout?.pages?.find(
        p => p.page_type === LANDING_PAGE
      );

      // Remove archived components
      const active = landingPage.components.filter(c => !c.archived);

      // Fixed components
      const header = active.find(c => c.component_type === 'header');
      const hero = active.find(c => c.component_type === 'hero_banner');
      const footer = active.find(c => c.component_type === 'footer');

      // Sort body components by existing order - USE PAGE_ORDER FROM BACKEND
      const body = active
        .filter(c => !['header', 'hero_banner', 'footer'].includes(c.component_type))
        .sort((a, b) => (a.order ?? 0) - (b.order ?? 0)); // Use page_order instead of order

      //Testing the front end
      // const body = active
      //   .filter(c => !['header', 'hero_banner', 'footer'].includes(c.component_type))
      //   .sort((a, b) => {
      //     console.log(`Sorting: ${a.component_type}(${a.order}) vs ${b.component_type}(${b.order})`);
      //     return (a.order ?? 0) - (b.order ?? 0);
      //   });


      // Rebuild the array
      const normalized = [
        ...(header ? [header] : []),
        ...(hero ? [hero] : []),
        ...body,
        ...(footer ? [footer] : [])
      ];

      // Reorder based on index
      normalized.forEach((component, index) => {
        component.order = index;
      });

      // Update Components
      landingPage.components = normalized;
    },
    archiveComponentInDraft(component) {
      const landingPage = this.draftLayout?.pages?.find(
        page => page.page_type === 'landing_page'
      );

      // Component Index
      const index = landingPage.components.findIndex(
        c => c.id === component.id
      );
      if (index === -1) return; // Ignore Global Settings

      // Archive / Remove Component
      component.archived = true;
      landingPage.components[index].archived = true;
      this.removedComponents.push(component);
      landingPage.components.splice(index, 1);
      this.normalizeLandingPageComponents();
    },
    archiveComponents() {
      this.removedComponents.forEach(component => {
        if (!component.id) return;
        return this.landingPageService.archiveComponent(
          this.organizationId,
          component.id
        );
      });
    },
  },
  getters: {
    getResourceById: state => (resourceId) => {
      return state.resources?.find(r => r.id === resourceId);
    },
    getFileNameByResourceId: state => (resourceId) => {
      const file = state.resources?.find(r => r.id === resourceId);
      return file ? file.file_name : "File not found";
    },

    primaryPallet(state) {
      return [
        {
          color: state.localBrand.color_primary_light,
          label: "Light"
        },
        {
          color: state.localBrand.color_primary_dark,
          label: "Dark"
        },
      ];
    },

    accentPallet(state) {
      return [
        {
          color: state.localBrand.color_accent_light,
          label: "Light"
        },
        {
          color: state.localBrand.color_accent_dark,
          label: "Dark"
        },
      ];
    },

    brandDirty(state) {
      return state.brand.color_primary !== state.localBrand.color_primary
        || state.brand.color_accent !== state.localBrand.color_accent
        || state.brand.logo?.id !== state.localBrand.logo?.id
        || state.brand.favicon?.id !== state.localBrand.favicon?.id;
    },

    sortableComponents(state) {
      const components = state.draftLayout?.pages?.find((page) => page.page_type === 'landing_page')?.components || [];
      return components.filter(component => !['header', 'footer', 'hero_banner'].includes(component.component_type));
    },

    cssVars(state) {
      const p = state.localBrand?.color_primary || DEFAULT_PRIMARY_COLOR;
      const pd = state.localBrand?.color_primary_dark || '#08602d';
      const a = state.localBrand?.color_accent || DEFAULT_ACCENT_COLOR;

      function textColorFor(c) {
        const tc = tinycolor(c);
        if (!tc.isValid()) return '#212121';
        return tc.getLuminance() > 0.5 ? '#212121' : '#EBEBEB';
      }

      const primaryText = textColorFor(p);
      const accentText = textColorFor(a);

      const faintBorder = '1px solid rgba(0,0,0,0.15)';
      const primaryBorder = primaryText === '#212121' ? faintBorder : '1px solid transparent';
      const accentBorder = accentText === '#212121' ? faintBorder : '1px solid transparent';

      return {
        '--brand-primary': p,
        '--brand-primary-dark': pd,
        '--brand-primary-text': primaryText,
        '--brand-primary-border': primaryBorder,

        '--brand-accent': a,
        '--brand-accent-text': accentText,
        '--brand-accent-border': accentBorder,
      };
    },
  }
});

